import { supabase } from './supabaseClient';
import { convertToWebp } from './imageUtils';

const MAX_SIZE_BYTES = 8 * 1024 * 1024;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_DIMENSION = 1600;

export async function uploadSiteImage(key: string, file: File): Promise<string> {
  if (!ALLOWED_TYPES.includes(file.type)) {
    throw new Error('Please choose a PNG, JPEG, WEBP, or GIF image.');
  }
  if (file.size > MAX_SIZE_BYTES) {
    throw new Error('Image must be smaller than 8MB.');
  }

  // Logos are often exported on a large empty canvas; crop the empty
  // margins so they render at a sensible size in the header and footer.
  const webp = await convertToWebp(file, MAX_DIMENSION, 0.85, /logo/i.test(key));
  // Unique path per upload (rather than upsert on a fixed path) — no
  // cache-busting query param needed, and old versions harmlessly age out.
  const path = `${key}/${Date.now()}.webp`;

  const { error: uploadError } = await supabase.storage.from('rxk-content').upload(path, webp, {
    contentType: 'image/webp',
  });
  if (uploadError) throw uploadError;

  const {
    data: { publicUrl },
  } = supabase.storage.from('rxk-content').getPublicUrl(path);

  const {
    data: { user },
  } = await supabase.auth.getUser();

  const { error: upsertError } = await supabase
    .from('rxk_site_images')
    .upsert({ key, url: publicUrl, updated_by: user?.id ?? null }, { onConflict: 'key' });
  if (upsertError) throw upsertError;

  return publicUrl;
}

const FAVICON_ALLOWED_TYPES = ['image/png', 'image/x-icon', 'image/vnd.microsoft.icon', 'image/svg+xml'];
const FAVICON_MAX_SIZE_BYTES = 1 * 1024 * 1024;

// Favicons are the one image upload that deliberately skips the WebP
// pipeline — browser tab icons need PNG/ICO/SVG for reliable cross-browser
// support (Safari in particular doesn't consistently honor a WebP favicon).
export async function uploadFaviconImage(file: File): Promise<string> {
  if (!FAVICON_ALLOWED_TYPES.includes(file.type)) {
    throw new Error('Please choose a PNG, ICO, or SVG image.');
  }
  if (file.size > FAVICON_MAX_SIZE_BYTES) {
    throw new Error('Favicon must be smaller than 1MB.');
  }

  const ext = file.type === 'image/svg+xml' ? 'svg' : file.type.includes('icon') ? 'ico' : 'png';
  const path = `seo.favicon/${Date.now()}.${ext}`;

  const { error: uploadError } = await supabase.storage.from('rxk-content').upload(path, file, {
    contentType: file.type,
  });
  if (uploadError) throw uploadError;

  const {
    data: { publicUrl },
  } = supabase.storage.from('rxk-content').getPublicUrl(path);

  const {
    data: { user },
  } = await supabase.auth.getUser();

  const { error: upsertError } = await supabase
    .from('rxk_site_images')
    .upsert({ key: 'seo.favicon', url: publicUrl, updated_by: user?.id ?? null }, { onConflict: 'key' });
  if (upsertError) throw upsertError;

  return publicUrl;
}
